/**
 * @voltxampere/components — Speaker dedicated component (Sprint 31-4)
 *
 * 2-pin loudspeaker. Audio output frequency derives from V_pin via the
 * @voltxampere/audio mapping (`speakerFrequency()` log-map V → Hz).
 * Sprint 31 ships the schema + audio mapping; the engine stamp is
 * scoped to Sprint 44 (COMPONENT_CATALOG_v3.2 §3.17 elektromekanik
 * group), so a circuit running Speaker through the F5 transient path
 * surfaces the "stamp not registered" error from `register.ts` until
 * the Sprint 44 stamp lands. Apps/web SPRINT_31_REPORT.md documents
 * this expected behaviour.
 *
 * Engine zero-diff invariant: this file is in `@voltxampere/components`,
 * not `@voltxampere/engine`. CORE_COMPONENT_DEFINITIONS registration
 * adds the type to the canonical registry but does NOT add a stamp;
 * the engine's `SCHEMA_SYMBOL_ALIASES` table is unchanged.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { z } from "zod";
export const SpeakerParamsSchema = z
    .object({
    /**
     * Nominal impedance in ohms. Typical hobbyist speakers: 4Ω / 8Ω /
     * 16Ω. Sprint 44 stamp will use this for the resistor-equivalent
     * MNA contribution; Sprint 31 audio mapping ignores it (frequency
     * derives from V_pin alone).
     */
    impedance: z.number().positive().finite().default(8),
    /**
     * Lower frequency bound for the V_pin → Hz mapping (Hz). Below
     * `V_threshold` the speaker is silent regardless. Default 200 Hz
     * matches the Sprint 31 `speakerFrequency()` floor.
     */
    fMin: z.number().positive().finite().default(200),
    /**
     * Upper frequency bound (Hz). Default 2000 Hz matches the Sprint
     * 31 `speakerFrequency()` ceiling — well within human-audible
     * range and below the Tone.js Oscillator clamp (5000 Hz).
     */
    fMax: z.number().positive().finite().default(2000),
})
    .strict()
    .refine((s) => s.fMax > s.fMin, {
    message: "Speaker fMax must be strictly greater than fMin",
    path: ["fMax"],
});
export const speakerDefinition = {
    type: "speaker",
    category: "passive",
    displayName: { tr: "Hoparlör", en: "Speaker" },
    symbol: "SPK",
    pins: [
        { id: "p1", side: "left", offset: 0, label: "+" },
        { id: "p2", side: "right", offset: 0, label: "-" },
    ],
    parameterSchema: SpeakerParamsSchema,
    nonlinear: false,
};
//# sourceMappingURL=speaker.js.map